<template lang="pug">
    .reservation-settings
        .reservation-settings__header
            h2 {{ $t('Settings') }}
            el-button(size="small" @click="goBack")
                i.el-icon-close
                |  {{ $t('Close') }}

        el-form(ref="form" label-position="top")
            el-card(shadow="never")
                .card-header(slot="header")
                    h3 {{ $t('Reservation Settings') }}

                el-form-item(:label="$t('Default Duration')")
                    el-select(:value="settings.defaultDuration" @input="updateSetting('defaultDuration', $event)" style="width: 200px" :disabled="!canEdit")
                        el-option(
                            v-for="opt in durationOptions"
                            :key="opt.value"
                            :label="opt.label"
                            :value="opt.value"
                        )

                el-form-item(:label="$t('Table Placement')")
                    el-radio-group(:value="settings.tablePlacementMode" @input="updateSetting('tablePlacementMode', $event)" :disabled="!canEdit")
                        el-radio-button(
                            v-for="opt in tablePlacementModeOptions"
                            :key="opt.value"
                            :label="opt.value"
                        ) {{ opt.label }}
                    .setting-hint--block {{ $t('Automatic picks the smallest matching table or combination on save') }}

                el-form-item
                    el-switch(
                        :value="settings.touchKeyboardAutoOpen"
                        @input="updateSetting('touchKeyboardAutoOpen', $event)"
                        :disabled="!canEdit"
                        :active-text="$t('Always show on-screen keyboard')"
                    )
                    .setting-hint--block {{ $t('Opens the keyboard in the reservation form as soon as a text field is tapped.') }}

                el-form-item(:label="$t('Button size')")
                    el-radio-group(:value="settings.presetButtonSize" @input="updateSetting('presetButtonSize', $event)" :disabled="!canEdit")
                        el-radio-button(
                            v-for="opt in presetButtonSizeOptions"
                            :key="opt.value"
                            :label="opt.value"
                        ) {{ opt.label }}
                    .setting-hint--block {{ $t('Size of the quick select buttons for date, time, duration and party size.') }}

                el-form-item(:label="$t('Tags for special requests')")
                    .note-tags
                        el-tag(
                            v-for="tag in noteTags"
                            :key="tag"
                            size="medium"
                            :closable="canEdit"
                            :disable-transitions="true"
                            @close="removeNoteTag(tag)"
                        ) {{ tag }}

                        el-input.note-tags__input(
                            v-if="canEdit && addingNoteTag"
                            ref="noteTagInput"
                            v-model="newNoteTag"
                            size="small"
                            :maxlength="60"
                            @keyup.enter.native="confirmNoteTag"
                            @blur="confirmNoteTag"
                        )
                        el-button(
                            v-else-if="canEdit"
                            size="small"
                            icon="el-icon-plus"
                            @click="startNoteTag"
                        ) {{ $t('Add tag') }}
                    .setting-hint--block {{ $t('Shown as quick select in the reservation form, e.g. birthday or funeral reception.') }}

            el-card(shadow="never" style="margin-top: 20px")
                .card-header(slot="header")
                    h3 {{ $t('Online reservations') }}

                el-form-item
                    el-switch(
                        :value="settings.onlineEnabled"
                        @input="updateSetting('onlineEnabled', $event)"
                        :disabled="!canEdit"
                        :active-text="$t('Accept reservations from the website')"
                    )
                    .setting-hint--block {{ $t('Guests pick party size, date and time in the reservation widget; free tables are checked live.') }}

                template(v-if="settings.onlineEnabled")
                    el-form-item(:label="$t('Reservation times')")
                        .service-times
                            .service-times__day(v-for="weekday in weekdayOptions" :key="weekday.value")
                                .service-times__label {{ weekday.label }}
                                .service-times__windows
                                    .service-times__window(v-for="entry in windowsOfWeekday(weekday.value)" :key="entry.index")
                                        el-time-select.service-times__time(
                                            :value="entry.window.from"
                                            :picker-options="timePickerOptions"
                                            :clearable="false"
                                            :disabled="!canEdit"
                                            size="small"
                                            @input="updateServiceTime(entry.index, 'from', $event)"
                                        )
                                        span.service-times__separator –
                                        el-time-select.service-times__time(
                                            :value="entry.window.to"
                                            :picker-options="timePickerOptions"
                                            :clearable="false"
                                            :disabled="!canEdit"
                                            size="small"
                                            @input="updateServiceTime(entry.index, 'to', $event)"
                                        )
                                        el-button(
                                            v-if="canEdit"
                                            size="mini"
                                            type="text"
                                            icon="el-icon-delete"
                                            @click="removeServiceTime(entry.index)"
                                        )
                                    el-button(
                                        v-if="canEdit"
                                        size="mini"
                                        icon="el-icon-plus"
                                        @click="addServiceTime(weekday.value)"
                                    ) {{ $t('Add time window') }}
                        .setting-hint--block {{ $t('Days without a time window cannot be booked online.') }}

                    el-form-item(:label="$t('Closed days')")
                        el-date-picker(
                            :value="settings.closedDates"
                            type="dates"
                            value-format="yyyy-MM-dd"
                            format="dd.MM.yyyy"
                            style="width: 100%; max-width: 420px"
                            :disabled="!canEdit"
                            :placeholder="$t('Select dates')"
                            @input="updateSetting('closedDates', $event || [])"
                        )
                        .setting-hint--block {{ $t('Holidays and company events. These days stay closed even with a time window.') }}

                    .settings-row
                        el-form-item(:label="$t('Time slot interval')")
                            el-select(
                                :value="settings.slotInterval"
                                style="width: 180px"
                                :disabled="!canEdit"
                                @input="updateSetting('slotInterval', $event)"
                            )
                                el-option(
                                    v-for="opt in slotIntervalOptions"
                                    :key="opt.value"
                                    :label="opt.label"
                                    :value="opt.value"
                                )

                        el-form-item(:label="$t('Lead time')")
                            el-select(
                                :value="settings.leadTime"
                                style="width: 180px"
                                :disabled="!canEdit"
                                @input="updateSetting('leadTime', $event)"
                            )
                                el-option(
                                    v-for="opt in leadTimeOptions"
                                    :key="opt.value"
                                    :label="opt.label"
                                    :value="opt.value"
                                )

                        el-form-item(:label="$t('Bookable in advance (days)')")
                            el-input-number(
                                :value="settings.maxAdvanceDays"
                                :min="1"
                                :max="730"
                                size="small"
                                :disabled="!canEdit"
                                @input="updateSetting('maxAdvanceDays', $event)"
                            )

                    .settings-row
                        el-form-item(:label="$t('Minimum party size')")
                            el-input-number(
                                :value="settings.minPartySize"
                                :min="1"
                                :max="100"
                                size="small"
                                :disabled="!canEdit"
                                @input="updateSetting('minPartySize', $event)"
                            )

                        el-form-item(:label="$t('Maximum party size')")
                            el-input-number(
                                :value="settings.maxPartySize"
                                :min="1"
                                :max="100"
                                size="small"
                                :disabled="!canEdit"
                                @input="updateSetting('maxPartySize', $event)"
                            )

                    el-form-item
                        el-switch(
                            :value="settings.autoConfirm"
                            @input="updateSetting('autoConfirm', $event)"
                            :disabled="!canEdit"
                            :active-text="$t('Confirm reservations automatically')"
                        )
                        .setting-hint--block {{ $t('Off: online reservations arrive as open requests and have to be confirmed in the board.') }}

                    el-form-item
                        el-switch(
                            :value="settings.guestConfirmationEnabled"
                            @input="updateSetting('guestConfirmationEnabled', $event)"
                            :disabled="!canEdit"
                            :active-text="$t('Send confirmation to the guest')"
                        )

                    el-form-item(:label="$t('Notify these addresses')")
                        .note-tags
                            el-tag(
                                v-for="email in notificationRecipients"
                                :key="email"
                                size="medium"
                                :closable="canEdit"
                                :disable-transitions="true"
                                @close="removeRecipient(email)"
                            ) {{ email }}

                            el-input.note-tags__input--wide(
                                v-if="canEdit && addingRecipient"
                                ref="recipientInput"
                                v-model="newRecipient"
                                size="small"
                                :maxlength="190"
                                @keyup.enter.native="confirmRecipient"
                                @blur="confirmRecipient"
                            )
                            el-button(
                                v-else-if="canEdit"
                                size="small"
                                icon="el-icon-plus"
                                @click="startRecipient"
                            ) {{ $t('Add address') }}
                        .setting-hint--block {{ $t('Receives an email for every online reservation.') }}

                    el-form-item(:label="$t('Note in the widget')")
                        el-input(
                            :value="settings.onlineHint"
                            type="textarea"
                            :rows="3"
                            :maxlength="2000"
                            :disabled="!canEdit"
                            @input="updateSetting('onlineHint', $event)"
                        )
                        .setting-hint--block {{ $t('Shown above the form, e.g. a note about large groups.') }}

            el-card(shadow="never" style="margin-top: 20px")
                .card-header(slot="header")
                    h3 {{ $t('Table Combinations') }}

                .combinations-empty(v-if="!combinations.length")
                    | {{ $t('No combinations defined yet. Create them in floor plan edit mode with Select tables or Ctrl/Cmd-click on desktop.') }}

                el-table(v-else :data="combinations" size="small" style="width: 100%")
                    el-table-column(prop="name" :label="$t('Name')")
                        template(slot-scope="scope")
                            span {{ scope.row.name || '—' }}
                    el-table-column(:label="$t('Tables')")
                        template(slot-scope="scope")
                            span {{ tablesLabel(scope.row) }}
                    el-table-column(:label="$t('Total Seats')" width="120")
                        template(slot-scope="scope")
                            span {{ totalSeats(scope.row) }}
                    el-table-column(:label="$t('Actions')" width="220" align="right")
                        template(slot-scope="scope")
                            .combination-actions(v-if="canEditFloorPlan")
                                el-button(
                                    size="mini"
                                    @click="renameCombination(scope.row)"
                                ) {{ $t('Rename') }}
                                el-button(
                                    size="mini"
                                    type="danger"
                                    @click="confirmDeleteCombination(scope.row)"
                                ) {{ $t('Delete') }}

            //- Save button
            .reservation-settings__footer
                el-button(
                    v-if="canEdit"
                    type="primary"
                    @click="save"
                    :loading="saving"
                ) {{ $t('Save Settings') }}

                transition(name="el-fade-in")
                    el-tag(v-if="saved" type="success" style="margin-left: 10px") {{ $t('Saved!') }}
</template>

<i18n>
{
    "de": {
        "Reservation Settings": "Reservierungseinstellungen",
        "Default Duration": "Standard-Dauer",
        "Settings": "Einstellungen",
        "Close": "Schließen",
        "Save Settings": "Einstellungen speichern",
        "Saved!": "Gespeichert!",
        "Settings saved": "Einstellungen gespeichert",
        "Table Placement": "Tischplatzierung",
        "Automatic picks the smallest matching table or combination on save": "Automatisch wählt beim Speichern den kleinsten passenden Tisch (oder die passende Kombination).",
        "Always show on-screen keyboard": "Bildschirmtastatur immer einblenden",
        "Button size": "Button-Größe",
        "Tags for special requests": "Tags für besondere Wünsche",
        "Add tag": "Tag hinzufügen",
        "Shown as quick select in the reservation form, e.g. birthday or funeral reception.": "Werden im Reservierungsformular als Schnellauswahl angezeigt, z. B. Geburtstag oder Trauerfeier.",
        "Size of the quick select buttons for date, time, duration and party size.": "Größe der Schnellauswahl-Buttons für Datum, Uhrzeit, Dauer und Personenanzahl.",
        "Opens the keyboard in the reservation form as soon as a text field is tapped.": "Öffnet die Tastatur im Reservierungsformular, sobald ein Textfeld angetippt wird.",
        "Online reservations": "Online-Reservierung",
        "Accept reservations from the website": "Reservierungen über die Website annehmen",
        "Guests pick party size, date and time in the reservation widget; free tables are checked live.": "Gäste wählen im Reservierungs-Widget Personenanzahl, Datum und Uhrzeit; freie Tische werden live geprüft.",
        "Reservation times": "Reservierungszeiten",
        "Add time window": "Zeitfenster hinzufügen",
        "Days without a time window cannot be booked online.": "Tage ohne Zeitfenster sind online nicht buchbar.",
        "Closed days": "Schließtage",
        "Select dates": "Daten wählen",
        "Holidays and company events. These days stay closed even with a time window.": "Feiertage und Betriebsferien. Diese Tage bleiben trotz Zeitfenster geschlossen.",
        "Time slot interval": "Takt der Uhrzeiten",
        "Lead time": "Vorlaufzeit",
        "Bookable in advance (days)": "Buchbar im Voraus (Tage)",
        "Minimum party size": "Personen mindestens",
        "Maximum party size": "Personen maximal",
        "Confirm reservations automatically": "Reservierungen automatisch bestätigen",
        "Off: online reservations arrive as open requests and have to be confirmed in the board.": "Aus: Online-Reservierungen kommen als offene Anfragen an und müssen im Board bestätigt werden.",
        "Send confirmation to the guest": "Bestätigung an den Gast senden",
        "Notify these addresses": "Benachrichtigung an",
        "Add address": "Adresse hinzufügen",
        "Receives an email for every online reservation.": "Erhält bei jeder Online-Reservierung eine E-Mail.",
        "Note in the widget": "Hinweis im Widget",
        "Shown above the form, e.g. a note about large groups.": "Wird über dem Formular angezeigt, z. B. ein Hinweis zu großen Gruppen.",
        "Please enter a valid email address": "Bitte eine gültige E-Mail-Adresse eingeben",
        "Table Combinations": "Tischkombinationen",
        "No combinations defined yet. Create them in floor plan edit mode with Select tables or Ctrl/Cmd-click on desktop.": "Noch keine Kombinationen vorhanden. Legen Sie sie im Tischplan-Bearbeitungsmodus mit \"Tische auswählen\" oder am Desktop per Strg/Cmd-Klick an.",
        "Name": "Name",
        "Tables": "Tische",
        "Total Seats": "Plätze gesamt",
        "Actions": "Aktionen",
        "Rename": "Umbenennen",
        "Delete": "Löschen",
        "Rename combination": "Kombination umbenennen",
        "New name": "Neuer Name",
        "OK": "OK",
        "Cancel": "Abbrechen",
        "Combination updated": "Kombination aktualisiert",
        "Delete combination?": "Kombination löschen?",
        "This action cannot be undone.": "Diese Aktion kann nicht rückgängig gemacht werden.",
        "Combination deleted": "Kombination gelöscht"
    }
}
</i18n>

<script>
import router from '@/router';
import { checkRestrictions } from '@/helpers';
import {
    useTableReservationSettingsStore,
    useTableReservationStore,
    useTableReservationFloorPlanStore
} from '@/stores';
import {
    durationOptions,
    leadTimeOptions,
    presetButtonSizeOptions,
    slotIntervalOptions,
    tablePlacementModeOptions,
    weekdayOptions
} from '@/stores/tableReservationSettings';

export default {
    name: 'ReservationSettings',
    data: function () {
        return {
            durationOptions: durationOptions,
            tablePlacementModeOptions: tablePlacementModeOptions,
            presetButtonSizeOptions: presetButtonSizeOptions,
            slotIntervalOptions: slotIntervalOptions,
            leadTimeOptions: leadTimeOptions,
            weekdayOptions: weekdayOptions,
            timePickerOptions: { start: '00:00', step: '00:15', end: '23:45' },
            addingNoteTag: false,
            newNoteTag: '',
            addingRecipient: false,
            newRecipient: '',
            saving: false
        };
    },
    computed: {
        store: function () {
            return useTableReservationSettingsStore();
        },
        reservationStore: function () {
            return useTableReservationStore();
        },
        floorPlanStore: function () {
            return useTableReservationFloorPlanStore();
        },
        settings: function () {
            return this.store.settings;
        },
        saved: function () {
            return this.store.saved;
        },
        noteTags: function () {
            return this.settings.noteTags || [];
        },
        notificationRecipients: function () {
            return this.settings.notificationRecipients || [];
        },
        serviceTimes: function () {
            return this.settings.serviceTimes || [];
        },
        canEdit: function () {
            return checkRestrictions(['ex:trsv:settings'], 'update');
        },
        canEditFloorPlan: function () {
            return checkRestrictions(['ex:trsv:floor-plan'], 'update');
        },
        combinations: function () {
            return this.floorPlanStore.combinations;
        },
        tablesById: function () {
            const map = {};
            this.floorPlanStore.tables.forEach((table) => {
                map[String(table.id)] = table;
            });
            return map;
        }
    },
    mounted: async function () {
        try {
            await this.reservationStore.ensureBoard();
        } catch (error) {
            this.showActionError(error);
        }
    },
    methods: {
        showActionError: function (error) {
            this.$message({
                message: error.message || this.$t('Something went wrong'),
                type: 'error',
                duration: 5000
            });
        },
        startNoteTag: function () {
            this.addingNoteTag = true;
            this.newNoteTag = '';
            this.$nextTick(() => {
                const input = this.$refs.noteTagInput;

                if (input) {
                    input.focus();
                }
            });
        },
        confirmNoteTag: function () {
            const tag = this.newNoteTag.trim();

            this.addingNoteTag = false;
            this.newNoteTag = '';

            if (!tag || this.noteTags.indexOf(tag) !== -1) {
                return;
            }

            this.updateSetting('noteTags', this.noteTags.concat([tag]));
        },
        removeNoteTag: function (tag) {
            if (!this.canEdit) {
                return;
            }

            this.updateSetting('noteTags', this.noteTags.filter((candidate) => {
                return candidate !== tag;
            }));
        },
        startRecipient: function () {
            this.addingRecipient = true;
            this.newRecipient = '';
            this.$nextTick(() => {
                const input = this.$refs.recipientInput;

                if (input) {
                    input.focus();
                }
            });
        },
        confirmRecipient: function () {
            const email = this.newRecipient.trim();

            this.addingRecipient = false;
            this.newRecipient = '';

            if (!email || this.notificationRecipients.indexOf(email) !== -1) {
                return;
            }

            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                this.$message({
                    message: this.$t('Please enter a valid email address'),
                    type: 'warning'
                });
                return;
            }

            this.updateSetting('notificationRecipients', this.notificationRecipients.concat([email]));
        },
        removeRecipient: function (email) {
            if (!this.canEdit) {
                return;
            }

            this.updateSetting('notificationRecipients', this.notificationRecipients.filter((candidate) => {
                return candidate !== email;
            }));
        },
        windowsOfWeekday: function (weekday) {
            return this.serviceTimes
                .map((window, index) => {
                    return { window: window, index: index };
                })
                .filter((entry) => {
                    return entry.window.weekday === weekday;
                });
        },
        addServiceTime: function (weekday) {
            const existing = this.windowsOfWeekday(weekday);
            const template = existing.length
                ? existing[existing.length - 1].window
                : { from: '18:00', to: '22:00' };

            this.updateSetting('serviceTimes', this.serviceTimes.concat([{
                weekday: weekday,
                from: template.from,
                to: template.to
            }]));
        },
        updateServiceTime: function (index, key, value) {
            const serviceTimes = this.serviceTimes.map((window, position) => {
                if (position !== index) {
                    return window;
                }

                const updated = { weekday: window.weekday, from: window.from, to: window.to };
                updated[key] = value;

                return updated;
            });

            this.updateSetting('serviceTimes', serviceTimes);
        },
        removeServiceTime: function (index) {
            this.updateSetting('serviceTimes', this.serviceTimes.filter((window, position) => {
                return position !== index;
            }));
        },
        updateSetting: function (key, value) {
            const data = {};
            data[key] = value;
            this.store.updateSettings(data);
        },
        goBack: function () {
            router.push({ name: 'table-reservations-overview' });
        },
        save: function () {
            this.saving = true;
            this.store.saveSettings().then(() => {
                this.saving = false;
                this.$message({
                    message: this.$t('Settings saved'),
                    type: 'success'
                });
            }).catch((error) => {
                this.saving = false;
                this.showActionError(error);
            });
        },
        tablesLabel: function (combination) {
            const names = this.floorPlanStore.prepareCombinationTableIds(combination.tableIds || []).map((id) => {
                const table = this.tablesById[String(id)];
                return table ? table.name : '?';
            });
            return names.join(' + ') || '—';
        },
        totalSeats: function (combination) {
            if (combination.totalSeats) {
                return combination.totalSeats;
            }
            return (combination.tableIds || []).reduce((sum, id) => {
                const table = this.tablesById[String(id)];
                return sum + (table ? (table.seats || 0) : 0);
            }, 0);
        },
        renameCombination: function (combination) {
            this.$prompt(this.$t('New name'), this.$t('Rename combination'), {
                confirmButtonText: this.$t('OK'),
                cancelButtonText: this.$t('Cancel'),
                inputValue: combination.name || ''
            }).then((result) => {
                return this.floorPlanStore.updateCombination(combination.id, { name: result.value || '' });
            }).then(() => {
                this.$message({ message: this.$t('Combination updated'), type: 'success' });
            }).catch((error) => {
                if (error === 'cancel' || error === 'close') {
                    return;
                }
                this.showActionError(error);
            });
        },
        confirmDeleteCombination: function (combination) {
            this.$confirm(
                this.$t('This action cannot be undone.'),
                this.$t('Delete combination?'),
                {
                    confirmButtonText: this.$t('Delete'),
                    cancelButtonText: this.$t('Cancel'),
                    type: 'warning'
                }
            ).then(() => {
                return this.floorPlanStore.deleteCombination(combination.id);
            }).then(() => {
                this.$message({ message: this.$t('Combination deleted'), type: 'success' });
            }).catch((error) => {
                if (error === 'cancel' || error === 'close') {
                    return;
                }
                this.showActionError(error);
            });
        }
    }
};
</script>

<style lang="sass" scoped>
.reservation-settings
    padding: 1rem

    &__header
        display: flex
        justify-content: space-between
        align-items: center
        margin-bottom: 20px

        h2
            margin: 0
            font-size: 1.2rem
            color: #303133

    &__footer
        display: flex
        align-items: center
        justify-content: flex-end
        margin-top: 24px
        padding: 16px 0

    h3
        margin: 0
        font-size: 1rem
        color: #303133

.setting-hint
    margin-left: 10px
    font-size: 0.85rem
    color: #909399

.setting-hint--block
    margin-top: 6px
    font-size: 0.85rem
    color: #909399
    line-height: 1.4

.note-tags
    display: flex
    flex-wrap: wrap
    align-items: center
    gap: 8px

    &__input
        width: 160px

    &__input--wide
        width: 240px

.settings-row
    display: flex
    flex-wrap: wrap
    align-items: flex-start
    gap: 24px

    .el-form-item
        margin-bottom: 22px

.service-times
    display: flex
    flex-direction: column
    gap: 8px
    max-width: 560px

    &__day
        display: flex
        align-items: flex-start
        gap: 12px
        padding: 6px 0
        border-bottom: 1px solid #ebeef5

        &:last-child
            border-bottom: 0

    &__label
        flex: 0 0 96px
        padding-top: 6px
        font-size: 0.9rem
        color: #606266

    &__windows
        display: flex
        flex-direction: column
        align-items: flex-start
        gap: 6px

    &__window
        display: flex
        align-items: center
        gap: 6px

    &__time
        width: 110px

    &__separator
        color: #909399

.combinations-empty
    color: #909399
    font-size: 0.9rem

.combination-actions
    display: flex
    justify-content: flex-end
    align-items: center
    flex-wrap: nowrap
    gap: 8px

    &::v-deep .el-button
        min-width: 96px

    &::v-deep .el-button + .el-button
        margin-left: 0

</style>
